<template>
    <div class="main-buttons">
        <template v-if="mode === 'send'">
            <span
                v-if="pkg.status === 'Draft'"
                class="clickable far fa-trash"
                @click="deleteDraft"
                :class="{ disabled: actionsDisabled }"
            ></span>
            <div class="button-group">
                <button @click="saveDraft" :disabled="actionsDisabled">Save as Draft</button>
                <div :class="{ 'split-button': submitAllowed }">
                    <button class="primary icon" @click="send" :disabled="actionsDisabled">
                        <span class="icon far fa-paper-plane"></span>Send
                    </button>
                    <button id="send-options" class="primary" v-if="submitAllowed" :disabled="actionsDisabled">
                        <span class="icon far fa-chevron-down"></span>
                    </button>
                    <elem-dropdown triggerSelector="#send-options">
                        <template v-slot:default="slotProps">
                            <div>
                                <selectable-list
                                    :items="[
                                        {
                                            name: payPrompt || 'Submit Now',
                                            value: 'submit',
                                            callback: submitNow,
                                        },
                                    ]"
                                    :active="slotProps.isOpen"
                                />
                            </div>
                        </template>
                    </elem-dropdown>
                </div>
            </div>
        </template>
        <template v-if="mode === 'post'">
            <span
                v-if="pkg.status === 'Draft'"
                class="clickable far fa-trash"
                @click="deleteDraft"
                :class="{ disabled: actionsDisabled }"
            ></span>
            <div class="button-group">
                <button @click="saveDraft" :disabled="actionsDisabled">Save as Draft</button>
                <submit-button class="primary icon" :click="getLink" :disabled="actionsDisabled">
                    <span class="icon far fa-link"></span>Get Link
                </submit-button>
            </div>
        </template>
        <template v-if="mode === 'review'">
            <template v-if="hasUnsavedChanges">
                <button class="primary" @click="saveUnarchiveConfirm" :disabled="actionsDisabled">Save Changes</button>
            </template>
            <template v-else>
                <template v-if="pkg.is_archived">
                    <span
                        class="clickable far fa-inbox"
                        @click="archive(false)"
                        title="Unarchive this package"
                        :class="{ disabled: actionsDisabled }"
                    ></span>
                    <span
                        v-if="user.hasRole(org.settings.deletePkgMinRole)"
                        class="clickable far fa-trash"
                        @click="deletePackage"
                        title="Delete this package"
                        :class="{ disabled: actionsDisabled }"
                    ></span>
                </template>
                <span
                    v-else
                    class="clickable far"
                    :class="{
                        disabled: actionsDisabled,
                        'fa-trash': pkg.is_public,
                        'fa-archive': !pkg.is_public,
                    }"
                    @click="archive(true)"
                    :title="pkg.is_public ? 'Delete public link' : 'Archive this package'"
                ></span>
                <span
                    class="clickable far fa-file-certificate"
                    @click="viewCertificate"
                    title="View certificate of completion"
                    v-if="!pkg.is_public"
                ></span>
                <div class="button-group">
                    <button v-if="hasAdpForm && !pkg.is_public" @click="adpXml">Export ADP XML</button>
                    <div v-if="!pkg.is_archived" :class="{ 'split-button': submitAllowed }">
                        <submit-button v-if="pkg.is_public" class="primary icon" :click="getLink">
                            <span class="icon far fa-link"></span>Get Link
                        </submit-button>
                        <button v-else class="primary icon" @click="send" :disabled="actionsDisabled">
                            <span class="icon far fa-paper-plane"></span>Send or Share
                        </button>
                        <button id="send-options" class="primary" v-if="submitAllowed" :disabled="actionsDisabled">
                            <span class="icon far fa-chevron-down"></span>
                        </button>
                        <elem-dropdown triggerSelector="#send-options">
                            <template v-slot:default="slotProps">
                                <div>
                                    <selectable-list
                                        :items="[
                                            {
                                                name: payPrompt || 'Submit Now',
                                                value: 'submit',
                                                callback: submitNow,
                                            },
                                        ]"
                                        :active="slotProps.isOpen"
                                    />
                                </div>
                            </template>
                        </elem-dropdown>
                    </div>
                </div>
            </template>
        </template>
        <template v-if="mode === 'edit'">
            <span
                v-if="pkg.is_archived"
                class="clickable far fa-inbox"
                @click="archive(false)"
                :class="{ disabled: actionsDisabled }"
            ></span>
            <span
                v-else
                class="clickable far fa-archive"
                @click="archive(true)"
                :class="{ disabled: actionsDisabled }"
            ></span>
            <div class="button-group">
                <button
                    v-if="hasUnsavedChanges"
                    class="primary"
                    @click="saveUnarchiveConfirm"
                    :disabled="actionsDisabled"
                >
                    Save Changes
                </button>
                <button v-else-if="hasAdpForm" @click="adpXml">Export ADP XML</button>
                <div v-if="submitAllowed" class="split-button">
                    <button class="primary icon" @click="submitNow" :disabled="actionsDisabled">
                        {{ payPrompt || `Submit to ${assignedToName || org.name}` }}
                    </button>
                    <button id="send-options" class="primary" :disabled="actionsDisabled">
                        <span class="icon far fa-chevron-down"></span>
                    </button>
                    <elem-dropdown triggerSelector="#send-options">
                        <template v-slot:default="slotProps">
                            <div>
                                <selectable-list
                                    :items="[
                                        {
                                            name: 'Send to Someone Else',
                                            value: 'send',
                                            callback: send,
                                        },
                                    ]"
                                    :active="slotProps.isOpen"
                                />
                            </div>
                        </template>
                    </elem-dropdown>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
    import { mapGetters } from 'vuex';
    import { sanitizeFilename } from '../../utils/strings';

    export default {
        name: 'OfficeMainButtons',
        props: {
            mode: {
                type: String,
                required: true,
            },
        },
        computed: {
            ...mapGetters({
                from: 'base/fromRoute',
                pkg: 'package/pkg',
                history: 'package/history',
                isPublic: 'package/isPublic',
                users: 'org/users',
                org: 'org/org',
                forms: 'form/forms',
                title: 'package/title',
                pkgLink: 'package/link',
                revisionRef: 'package/revisionRef',
                user: 'user/user',
                assignedToUserId: 'office/assignedToUserId',
                assignedToBoxId: 'office/assignedToBoxId',
                isPaymentRequired: 'filler/isPaymentRequired',
                incompleteRequiredFieldsBefore: 'filler/incompleteRequiredFieldsBefore',
                boxes: 'boxes/boxes',
            }),
            assignedToName() {
                if (this.pkg.assigned_to_user_id) {
                    return this.users[this.pkg.assigned_to_user_id].name;
                } else if (this.pkg.assigned_to_box_id) {
                    return this.boxes.find((box) => box.id === this.pkg.assigned_to_box_id).name;
                }

                return '';
            },
            submitAllowed() {
                return this.pkg.status !== 'Active' && !this.pkg.is_archived && !this.pkg.is_public;
            },
            hasUnsavedChanges() {
                return this.$store.getters['office/hasUnsavedChanges'] || this.$store.getters['filler/hasUnsavedChanges'];
            },
            hasAdpForm() {
                return this.forms.some((f) => f.data.featureFlags.ADP);
            },
            actionsDisabled() {
                return !!this.pkg.device_token;
            },
            payPrompt() {
                return this.isPaymentRequired ? 'Save & Proceed to Payment' : '';
            },
        },
        methods: {
            async submitNow() {
                if (!(await this.$store.dispatch('package/validateFieldValues'))) return;

                // only check for required-before-submit fields if this is an internal filler
                if (this.mode === 'edit') {
                    let errors = this.incompleteRequiredFieldsBefore('before_submit');

                    if (errors.length) {
                        let ids = errors.map((e) => e.id);
                        return this.$store.dispatch('filler/markInvalidInputs', {
                            ids,
                            message: errors.length === 1 ? errors[0].message : 'Please complete required fields',
                        });
                    }
                }

                if (this.assignedToUserId === this.user.id) {
                    let confirm = await this.$store.dispatch('dialog/set', {
                        type: 'confirm',
                        header: 'Confirm Submission',
                        body: 'This package is assigned to you. Are you sure you want to submit to yourself?',
                    });
                    if (!confirm) return;
                }
                
                let start = Date.now();
                this.$loading('Submitting Form', false);
                await this.$store.dispatch('package/save', 'submit');
                await this.$timeout(750, start);
                this.$loading(false);
                this.$store.dispatch('office/changeMode', 'review');
                this.$store.dispatch('package/loadHistory');
            },
            async saveDraft() {
                if (!(await this.$store.dispatch('package/validateFieldValues'))) return;
                if (!(await this.$store.dispatch('package/confirmNoAssignee'))) return;
                
                let start = Date.now();
                this.$loading('Saving Draft', false);
                await this.$store.dispatch('package/save', 'draft');
                await this.$timeout(750, start);
                this.$loading(false);
                this.$store.dispatch('notification/success', 'Draft saved');
            },
            async send() {
                if (!this.pkg.sequence.length) {
                    return this.$store.dispatch('notification/set', {
                        type: 'error',
                        message: 'Cannot send an empty package',
                        timeout: 4000,
                    });
                }

                if (!(await this.$store.dispatch('package/validateFieldValues'))) return;
                if (!(await this.$store.dispatch('package/confirmNoAssignee'))) return;
                this.$store.dispatch('package/sendFormDialog');
            },
            async saveUnarchiveConfirm() {
                if (!(await this.$store.dispatch('package/validateFieldValues'))) return;

                this.$loading();
                await this.$store.dispatch('package/save', 'save');

                let unarchive = false;

                if (this.pkg.is_archived) {
                    this.$loading(false);
                    unarchive = await this.$store.dispatch('dialog/set', {
                        type: 'confirm',
                        header: 'Unarchive?',
                        body: 'Changes have been saved. Do you want to unarchive this package?',
                        okText: 'Yes',
                        cancelText: 'No',
                    });
                }

                if (unarchive) {
                    this.$loading();
                    await this.$store.dispatch('package/archivePkgs', {
                        packageIds: [this.pkg.id],
                        archive: false,
                        skipConfirm: true,
                    });
                    this.$store.commit('package/isArchived', false);
                }

                this.$loading(false);
            },
            async getLink() {
                if (!(await this.$store.dispatch('package/validateFieldValues'))) return;
                if (!(await this.$store.dispatch('package/confirmNoAssignee'))) return;

                // Save the current pre-filled form data before showing link
                let start = Date.now();
                this.$loading('Creating Link', false);
                try {
                    let res = await this.$store.dispatch('package/save', 'send');
                    this.$store.dispatch('package/sendFormDialog', {
                        publicLink: this.pkgLink,
                        requiresReload: res && res.action === 'save',
                    });
                } finally {
                    await this.$timeout(750, start);
                    this.$loading(false);
                }
            },
            async deleteDraft() {
                let wasDeleted = await this.$store.dispatch('package/deleteDrafts', { packageIds: [this.pkg.id] });
                if (wasDeleted) {
                    this.$store.commit('filler/hasUnsavedChanges', false);
                    this.leave();
                }
            },
            async archive(archive) {
                let skipConfirm = false;
                if (this.pkg.is_public) {
                    let res = await this.$store.dispatch('dialog/set', {
                        type: 'confirm',
                        header: `Delete public link?`,
                        critical: true,
                        body: `This cannot be undone.`,
                    });
                    if (!res) return;
                    skipConfirm = true;
                }
                let res = await this.$store.dispatch('package/archivePkgs', {
                    packageIds: [this.pkg.id],
                    archive,
                    skipConfirm,
                });
                if (!res) return;

                if (archive) {
                    this.leave();
                } else {
                    this.$store.commit('package/isArchived', false);
                }
            },
            async deletePackage() {
                let res = await this.$store.dispatch('package/deletePackages', [this.pkg.id]);
                if (res) this.leave();
            },
            leave(listName = '') {
                let path;
                if (listName) {
                    path = `/boxes/${listName}`;
                } else {
                    path = this.from ? this.from.path : '/boxes';
                }
                this.$router.push(path);
            },
            viewCertificate() {
                this.$api.download(`packages/ref/${this.revisionRef}/certificate`, null, { open: true });
            },
            async adpXml() {
                let filename = sanitizeFilename(this.title) + '.xml';
                try {
                    this.$api.download(`/packages/adpXml/${this.pkg.id}`, null, { filename });
                } catch (err) {
                    this.$store.dispatch('notification/clear');
                    this.$store.dispatch('dialog/set', {
                        type: 'alert',
                        header: 'Unable to Generate XML',
                        body: err.message,
                    });
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .clickable {
        @include clickable;

        font-size: $font-size-larger;
        margin-right: 1em;
        height: 34px;

        &.disabled {
            pointer-events: none;
            color: $color-disabled-text;
        }
    }
</style>